import { getConversationsForPage } from '@/api/fbapi';
import { useAuth } from '@/contexts/AuthContext';
import { FbConversation } from '@/types/fb-type';
import { Search, UserCheck, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationList } from './ConversationList';

interface ChatListProp {
  conversations?: FbConversation[];
  loadingConversations: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  currentConversation?: FbConversation | null;
  onSelectConversation: (conversation: FbConversation) => void;
  onLoadMore?: () => void;
  refreshSignal?: { convId?: string; ts: number };
}

export default function ChatList({
  onSelectConversation,
  currentConversation,
  conversations = [],
  loadingConversations,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
}: ChatListProp) {
  const { t } = useTranslation('chat');
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedToMeOnly, setAssignedToMeOnly] = useState(false);
  const [assignedConversations, setAssignedConversations] = useState<FbConversation[] | null>(null);
  const [loadingAssigned, setLoadingAssigned] = useState(false);

  useEffect(() => {
    if (!assignedToMeOnly) {
      setAssignedConversations(null);
      return;
    }
    let cancelled = false;
    setLoadingAssigned(true);
    getConversationsForPage(undefined, true)
      .then((res) => {
        if (!cancelled && res.success) {
          setAssignedConversations(res.data?.data ?? []);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingAssigned(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assignedToMeOnly]);

  const baseList = useMemo(() => {
    if (!assignedToMeOnly) return conversations;
    const username = user?.username;
    const fromCurrent = conversations.filter((c) => c.isAssignedToUser(username));
    if (!assignedConversations) return fromCurrent;
    const map = new Map<string, FbConversation>();
    for (const c of fromCurrent) {
      if (c?.id) map.set(c.id, c);
    }
    for (const c of assignedConversations) {
      if (c?.id && !map.has(c.id)) map.set(c.id, c);
    }
    return Array.from(map.values());
  }, [assignedToMeOnly, conversations, assignedConversations, user?.username]);

  const filtered = searchQuery.trim()
    ? baseList.filter((c) => {
        const name = c.getUser()?.name?.toLowerCase() ?? '';
        const snippet = (c.snippet ?? '').toLowerCase();
        const q = searchQuery.toLowerCase();
        return name.includes(q) || snippet.includes(q);
      })
    : baseList;

  return (
    <div className="flex flex-col h-full min-h-0 w-full overflow-hidden">
      {/* Search bar + Filter */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-100 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          <input
            type="text"
            placeholder={t('search') || 'Search conversations…'}
            className="w-full pl-9 pr-8 py-2 rounded-full bg-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition placeholder-gray-400"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setAssignedToMeOnly(false)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${
              !assignedToMeOnly
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setAssignedToMeOnly(true)}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition ${
              assignedToMeOnly
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <UserCheck className="w-3.5 h-3.5" />
            Assign to me
          </button>
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ConversationList
          items={filtered}
          activeId={currentConversation?.id ?? null}
          onSelect={onSelectConversation}
          isLoading={loadingConversations || isLoadingMore || loadingAssigned}
          onEndReached={!assignedToMeOnly && hasMore && !isLoadingMore ? onLoadMore : undefined}
          emptyLabel={
            assignedToMeOnly
              ? 'No conversations assigned to you'
              : searchQuery
                ? t('noResults') || 'No results'
                : t('noConversations') || 'No conversations yet'
          }
        />
      </div>
    </div>
  );
}
